import React, { useState, useEffect } from "react";
import {
    ShoppingCart,
    Star,
    Package,
    ShieldCheck,
    ArrowRight,
    Plus,
    Minus,
    Music,
    Play,
    Pause,
    RotateCcw,
    Loader2,
    FileText,
    List,
    Archive
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import JazzHeader from "../homepage_components/JazzHeader";
import JazzFooter from "../homepage_components/JazzFooter";
import SEO from "../components/SEO";
import { ShopService } from "../services/user/ShopServices";
import { useCart } from "../context/CartContext";
import { useNotification } from "../context/NotificationContext";

export default function ProductDetails() {
    const { slug } = useParams();
    const { addToCart } = useCart();
    const { showNotification } = useNotification();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeImage, setActiveImage] = useState(0);
    const [quantity, setQuantity] = useState(1);
    const [relatedProducts, setRelatedProducts] = useState([]);

    // Audio States
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoadingAudio, setIsLoadingAudio] = useState(false);
    const [audioError, setAudioError] = useState(false);
    const [audioProgress, setAudioProgress] = useState(0);
    const audioRef = React.useRef(null);
    const playPromiseRef = React.useRef(null);

    // Reset audio on ID change
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsPlaying(false);
            setAudioProgress(0);
            setIsLoadingAudio(false);
            setAudioError(false);
        }
    }, [slug]);

    useEffect(() => {
        const fetchProduct = async () => {
            setLoading(true);
            try {
                const data = await ShopService.getProductBySlug(slug);
                setProduct(data);

                // Fetch related products (same category)
                const relatedData = await ShopService.getProducts({
                    category_id: data.category_id,
                    per_page: 4
                });
                setRelatedProducts((relatedData?.products || []).filter(p => (p.slug !== slug && p.id !== data.id)));

                setError(null);
            } catch (err) {
                setError("Product not found.");
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [slug]);

    const isMusic = product?.category_type === "Music Content";

    const handleAddToCart = () => {
        if (!product) return;
        addToCart(product, quantity);
    };

    const togglePlay = async () => {
        if (!audioRef.current) return;

        try {
            if (isPlaying) {
                if (playPromiseRef.current) {
                    await playPromiseRef.current;
                }
                audioRef.current.pause();
                setIsPlaying(false);
            } else {
                setAudioError(false);
                setIsLoadingAudio(true);
                playPromiseRef.current = audioRef.current.play();
                await playPromiseRef.current;
                setIsPlaying(true);
                setIsLoadingAudio(false);
            }
        } catch (err) {
            console.error("Audio playback error:", err);
            setAudioError(true);
            setIsPlaying(false);
            setIsLoadingAudio(false);
        } finally {
            playPromiseRef.current = null;
        }
    };

    const handleTimeUpdate = () => {
        if (!audioRef.current) return;
        const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
        setAudioProgress(progress);
    };

    const handleAudioEnded = () => {
        setIsPlaying(false);
        setAudioProgress(0);
    };

    const restartAudio = async () => {
        if (!audioRef.current) return;

        try {
            audioRef.current.currentTime = 0;
            if (!isPlaying) {
                setIsLoadingAudio(true);
                playPromiseRef.current = audioRef.current.play();
                await playPromiseRef.current;
                setIsPlaying(true);
                setIsLoadingAudio(false);
            }
        } catch (err) {
            setAudioError(true);
            setIsLoadingAudio(false);
        } finally {
            playPromiseRef.current = null;
        }
    };

    const handleAudioLoadStart = () => {
        setIsLoadingAudio(true);
    };

    const handleAudioCanPlay = () => {
        setIsLoadingAudio(false);
    };

    const handleAudioError = () => {
        setAudioError(true);
        setIsLoadingAudio(false);
        setIsPlaying(false);
    };

    if (loading) return (
        <div className="min-h-screen bg-[#fafafa]">
            <JazzHeader />
            <main className="pt-24 sm:pt-32 pb-16 sm:pb-24">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 animate-pulse">
                    <div className="h-4 w-48 bg-gray-200 mb-8 rounded" />
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
                        <div className="space-y-6">
                            <div className="aspect-[4/5] bg-white rounded-[2rem] border border-black/5" />
                            <div className="grid grid-cols-4 gap-4">
                                {[1, 2, 3, 4].map(i => <div key={i} className="aspect-square bg-gray-200 rounded-2xl" />)}
                            </div>
                        </div>
                        <div className="flex flex-col space-y-8">
                            <div className="space-y-4">
                                <div className="h-6 w-32 bg-gray-200 rounded-full" />
                                <div className="h-16 w-3/4 bg-gray-200 rounded" />
                                <div className="h-8 w-48 bg-gray-200 rounded" />
                            </div>
                            <div className="h-32 w-full bg-gray-200 shadow-inner italic p-6 space-y-2 rounded-2xl">
                                <div className="h-4 w-full bg-gray-300 rounded" />
                                <div className="h-4 w-full bg-gray-300 rounded" />
                            </div>
                            <div className="h-40 w-full bg-white border border-black/10 rounded-[2rem] p-8 space-y-4 shadow-sm">
                                <div className="h-4 w-32 bg-gray-200 rounded" />
                                <div className="h-16 w-full bg-gray-100 rounded-2xl" />
                            </div>
                            <div className="flex gap-4">
                                <div className="h-16 w-32 bg-white border border-black/10 rounded-2xl" />
                                <div className="h-16 flex-1 bg-gray-200 rounded-2xl" />
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <JazzFooter />
        </div>
    );

    if (error || !product) return (
        <div className="min-h-screen bg-[#fafafa]">
            <JazzHeader />
            <div className="pt-32 text-center px-4">
                <Package size={48} className="mx-auto text-black/20 mb-6" />
                <h2 className="text-3xl font-serif italic mb-4">{error || "Product not found"}</h2>
                <Link to="/shop" className="text-jazz-brown font-bold tracking-widest uppercase text-xs hover:underline">
                    Back to Shop
                </Link>
            </div>
            <JazzFooter />
        </div>
    );

    const images = (product?.images && product?.images?.length > 0) ? product.images : [product?.image];

    /**
     * Renders a product description as properly spaced paragraphs.
     * Handles:
     *  - Literal \n escape sequences stored in the DB as strings
     *  - Real newline characters (\n)
     *  - Paragraph grouping on double newlines (empty lines)
     */
    const renderDescription = (text) => {
        if (!text) return null;
        // Normalise literal backslash-n sequences
        const normalised = text.replace(/\\n/g, '\n');

        // 1. Initial split by newlines
        let blocks = normalised.split(/\n+/).filter(block => block.trim().length > 0);

        // 2. If it's still a single block, it might be a "wall of text" with only full stops.
        // We split on periods followed by a space and a capital letter.
        if (blocks.length === 1 && blocks[0].length > 100) {
            // Regex: match space after a period that is followed by a capital letter
            const sentenceSplit = blocks[0].split(/(?<=\.)\s+(?=[A-Z])/);
            if (sentenceSplit.length > 1) {
                blocks = sentenceSplit;
            }
        }

        return (
            <div className="mb-10 max-w-xl space-y-5">
                {blocks.map((block, i) => (
                    <p key={i} className="text-black font-medium text-lg leading-relaxed font-serif italic whitespace-pre-line last:mb-0">
                        {block.trim()}
                    </p>
                ))}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-[#fafafa]">
            <SEO
                title={product?.name || "Product Details"}
                description={product?.description}
                image={product?.image}
                url={`/shop/product/${slug}`}
                type="product"
            />
            <JazzHeader />

            <main className="pt-24 sm:pt-32 pb-16 sm:pb-24">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6">

                    {/* Breadcrumbs */}
                    <div className="mb-8 flex items-center gap-2 text-[10px] font-bold tracking-[2px] uppercase text-black/80">
                        <Link to="/" className="hover:text-black transition-colors">Home</Link>
                        <span>/</span>
                        <Link to="/shop" className="hover:text-black transition-colors">Shop</Link>
                        <span>/</span>
                        <span className="text-jazz-brown">{product.name}</span>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">

                        {/* Image Gallery */}
                        <div className="space-y-6">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="aspect-[4/5] bg-white rounded-[2rem] border border-black/5 overflow-hidden shadow-xl"
                            >
                                <img
                                    src={images[activeImage] || "/api/placeholder/600/750"}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                />
                            </motion.div>

                            {images && images.length > 1 && (
                                <div className="grid grid-cols-4 gap-4">
                                    {images.map((img, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setActiveImage(idx)}
                                            className={`aspect-square rounded-2xl overflow-hidden border-2 transition-all duration-300 ${activeImage === idx ? "border-jazz-brown scale-95 shadow-md" : "border-transparent opacity-60 hover:opacity-100"
                                                }`}
                                        >
                                            <img src={img || "/api/placeholder/150/150"} alt="" className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Product Info */}
                        <div className="flex flex-col">
                            <div className="mb-10">
                                <div className="flex items-center gap-4 mb-4">
                                    <span className="px-4 py-1.5 bg-jazz-brown/10 text-jazz-brown text-[10px] font-bold tracking-[2px] uppercase rounded-full">
                                        {product.category_name}
                                    </span>
                                    {product.is_popular && (
                                        <span className="flex items-center gap-1 text-[10px] font-bold text-amber-500 tracking-[1px] uppercase">
                                            <Star size={12} fill="currentColor" /> Best Seller
                                        </span>
                                    )}
                                </div>

                                <h1
                                    className="text-4xl sm:text-5xl lg:text-6xl font-normal text-black mb-6 leading-tight"
                                    style={{ fontFamily: "Playfair Display, serif" }}
                                >
                                    {product.name}
                                </h1>

                                <div className="flex items-end gap-4 mb-8">
                                    <span className="text-3xl font-bold text-black font-serif">
                                        ${Number(product.price).toFixed(2)}
                                    </span>
                                    <span className="text-black/80 text-sm mb-1 line-through font-serif">
                                        ${(Number(product.price) * 1.2).toFixed(2)}
                                    </span>
                                </div>

                                {renderDescription(product.description)}

                                <div className="space-y-4 mb-10">
                                    {/* <div className="flex items-center gap-3 text-black/40">
                                        <Truck size={18} />
                                        <p className="text-xs font-bold tracking-[1px] uppercase">Free global shipping on orders over $150</p>
                                    </div> */}
                                    <div className="flex items-center gap-3 text-black/80">
                                        <ShieldCheck size={18} />
                                        <p className="text-xs font-bold tracking-[1px] uppercase">Secure checkout with Stripe</p>
                                    </div>
                                </div>

                                {/* Audio Preview Section */}
                                {isMusic && (
                                    <div className="space-y-6 mb-10">
                                        {/* Audio Preview Section */}
                                        {product.has_audio && (
                                            <div className="p-8 bg-white border border-black/5 rounded-[2rem] shadow-sm overflow-hidden relative group">
                                                <div className="absolute top-0 right-0 p-4 opacity-5">
                                                    <Music size={80} />
                                                </div>

                                                <div className="relative z-10">
                                                    <div className="flex items-center justify-between mb-6">
                                                        <div>
                                                            <h4 className="text-[10px] font-bold tracking-[2px] uppercase text-jazz-brown mb-1">Preview Clip</h4>
                                                            <p className="text-xl font-serif italic text-black">Listen to Sample</p>
                                                        </div>
                                                        <div className="w-10 h-10 rounded-full bg-jazz-brown/10 flex items-center justify-center text-jazz-brown">
                                                            <Music size={18} />
                                                        </div>
                                                    </div>

                                                    <div className="bg-[#fafafa] p-6 rounded-2xl border border-black/5">
                                                        <div className="flex items-center gap-6">
                                                            <button
                                                                onClick={togglePlay}
                                                                disabled={isLoadingAudio}
                                                                className="w-14 h-14 rounded-full bg-black text-white flex items-center justify-center hover:bg-jazz-brown transition-all duration-300 shadow-lg group-hover:scale-105 disabled:opacity-50"
                                                            >
                                                                {isLoadingAudio ? (
                                                                    <Loader2 size={24} className="animate-spin" />
                                                                ) : isPlaying ? (
                                                                    <Pause size={24} fill="currentColor" />
                                                                ) : (
                                                                    <Play size={24} fill="currentColor" className="ml-1" />
                                                                )}
                                                            </button>

                                                            <div className="flex-1">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <span className="text-[10px] font-bold tracking-[1px] uppercase text-black/60">
                                                                        {isLoadingAudio ? "Loading..." : audioError ? "Error loading preview" : isPlaying ? "Playing Preview" : "Ready to Play"}
                                                                    </span>
                                                                    <button
                                                                        onClick={restartAudio}
                                                                        disabled={isLoadingAudio}
                                                                        className="text-black/40 hover:text-black transition-colors disabled:opacity-30"
                                                                    >
                                                                        <RotateCcw size={14} />
                                                                    </button>
                                                                </div>
                                                                <div className="h-1.5 w-full bg-black/5 rounded-full overflow-hidden">
                                                                    <motion.div
                                                                        className={`h-full ${audioError ? "bg-red-400" : "bg-jazz-brown"}`}
                                                                        initial={{ width: 0 }}
                                                                        animate={{ width: `${audioProgress}%` }}
                                                                        transition={{ type: "spring", bounce: 0, duration: 0.2 }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <audio
                                                            ref={audioRef}
                                                            src={ShopService.getAudioPreviewUrl(product.id)}
                                                            onTimeUpdate={handleTimeUpdate}
                                                            onEnded={handleAudioEnded}
                                                            onLoadStart={handleAudioLoadStart}
                                                            onCanPlay={handleAudioCanPlay}
                                                            onError={handleAudioError}
                                                            preload="auto"
                                                            className="hidden"
                                                            crossOrigin="anonymous"
                                                        />

                                                        <div className="mt-4 flex items-center justify-between">
                                                            <span className="text-[9px] font-bold tracking-[1px] uppercase text-black/60 flex items-center gap-1.5">
                                                                <Play size={10} /> 10-Second Extract
                                                            </span>
                                                            <span className="text-[9px] font-bold tracking-[1px] uppercase text-black/40 italic">
                                                                Full track available after purchase
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Album Tracklist Section */}
                                        {product.category_name.toLowerCase().includes('album') && product.track_list && product.track_list.length > 0 && (
                                            <div className="mt-6 p-8 bg-white border border-black/5 rounded-[2rem] shadow-sm">
                                                <div className="flex items-center gap-2 mb-6">
                                                    <List size={14} className="text-jazz-brown" />
                                                    <h4 className="text-[10px] font-bold tracking-[2px] uppercase text-black">Album Tracklist</h4>
                                                </div>
                                                <div className="space-y-1">
                                                    {product.track_list.map((track, idx) => (
                                                        <div key={idx} className="flex items-center gap-4 py-3 border-b border-black/5 last:border-0 font-serif group/track transition-colors hover:bg-black/5 rounded-lg px-2 -mx-2">
                                                            <span className="text-jazz-brown font-bold text-xs w-5">{String(idx + 1).padStart(2, '0')}</span>
                                                            <span className="text-black font-medium text-sm">{track}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Digital Item Info Section (PDF/ZIP) */}
                                        {(product.has_pdf || product.has_zip) && (
                                            <div className="p-8 bg-black/5 border border-black/5 rounded-[2rem] relative overflow-hidden group">
                                                <div className="absolute top-0 right-0 p-4 opacity-5">
                                                    {product.has_zip ? <Archive size={80} /> : <FileText size={80} />}
                                                </div>
                                                <div className="relative z-10 flex items-start gap-6">
                                                    <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center text-jazz-brown shadow-sm flex-shrink-0">
                                                        {product.has_zip ? <Archive size={24} /> : <FileText size={24} />}
                                                    </div>
                                                    <div>
                                                        <h4 className="text-[10px] font-bold tracking-[2px] uppercase text-jazz-brown mb-2 text-wrap pr-10">
                                                            Digital Delivery / {product.has_zip ? 'Archive' : 'Digital Doc'}
                                                        </h4>
                                                        <p className="text-black text-sm font-medium leading-relaxed italic mb-1">
                                                            {product.category_name.toLowerCase().includes('album')
                                                                ? `This item includes a ${product.has_zip ? 'ZIP file archive' : 'digital download'} of the album. Your download will be available in your personal dashboard immediately after a successful purchase.`
                                                                : product.category_name.toLowerCase().includes('sheet')
                                                                    ? `This item includes a ${product.has_zip ? 'ZIP file archive' : 'PDF document'}. Your download will be available in your personal dashboard immediately after a successful purchase.`
                                                                    : `This item includes a ${product.has_zip ? 'ZIP archive' : product.has_pdf ? 'PDF document' : 'digital file'}. Your download will be available in your personal dashboard immediately after a successful purchase.`
                                                            }
                                                        </p>
                                                        <p className="text-black/40 text-[10px] font-bold tracking-[1px] uppercase">
                                                            Instant Digital Delivery
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Actions */}
                                {(product.stock_quantity > 0 || isMusic) ? (
                                    <div className="flex flex-col sm:flex-row items-stretch gap-4">
                                        {!isMusic && (
                                            <div className="flex items-center bg-white border border-black/10 rounded-2xl px-4 py-2">
                                                <button
                                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                                    className="p-2 hover:text-jazz-brown transition-colors"
                                                >
                                                    <Minus size={16} />
                                                </button>
                                                <span className="w-12 text-center font-bold text-lg">{quantity}</span>
                                                <button
                                                    onClick={() => {
                                                        if (product.stock_quantity && quantity >= product.stock_quantity) {
                                                            showNotification({
                                                                type: "warning",
                                                                title: "Stock Limit",
                                                                message: `Only ${product.stock_quantity} available in stock.`
                                                            });
                                                            return;
                                                        }
                                                        setQuantity(quantity + 1);
                                                    }}
                                                    className="p-2 hover:text-jazz-brown transition-colors"
                                                >
                                                    <Plus size={16} />
                                                </button>
                                            </div>
                                        )}
                                        <button
                                            onClick={handleAddToCart}
                                            className="flex-1 bg-black text-white px-10 py-5 rounded-2xl flex items-center justify-center gap-3 text-xs font-bold tracking-[3px] uppercase hover:bg-jazz-brown transition-all duration-500 shadow-xl group"
                                        >
                                            <ShoppingCart size={18} className="group-hover:translate-x-1 transition-transform" />
                                            Add to Cart
                                        </button>
                                    </div>
                                ) : (
                                    <div className="p-6 bg-red-50 border border-red-100 rounded-2xl text-red-500 text-center">
                                        <p className="text-xs font-bold tracking-[2px] uppercase">This item is currently sold out</p>
                                    </div>
                                )}
                            </div>

                            {/* Additional Details Accordion */}
                            <div className="mt-auto pt-10 border-t border-black/5 grid grid-cols-1 sm:grid-cols-2 gap-8">
                                <div>
                                    <h4 className="text-[10px] font-bold tracking-[2px] uppercase text-black mb-4">Specifications</h4>
                                    <ul className="space-y-2">
                                        <li className="text-xs text-black font-medium flex justify-between">
                                            <span>Format</span>
                                            <span className="font-bold">{isMusic ? "Digital" : "Physical"}</span>
                                        </li>
                                        <li className="text-xs text-black font-medium flex justify-between">
                                            <span>Collection</span>
                                            <span className="font-bold">{product.category_type}</span>
                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="text-[10px] font-bold tracking-[2px] uppercase text-black/60 mb-4">Availability</h4>
                                    <p className="text-xs text-black/70 italic">
                                        {isMusic
                                            ? "Available for digital download."
                                            : "In stock ready to ship globally within 3-5 business days."}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Related Products */}
                    {relatedProducts && relatedProducts.length > 0 && (
                        <div className="mt-32">
                            <div className="flex items-end justify-between mb-12">
                                <div>
                                    <h2 className="text-3xl font-serif italic mb-2">You might also like</h2>
                                    <p className="text-black/60 text-[10px] font-bold tracking-[2px] uppercase">Curated collection</p>
                                </div>
                                <Link to="/shop" className="text-black/60 hover:text-jazz-brown transition-colors flex items-center gap-2 group text-[10px] font-bold tracking-[2px] uppercase">
                                    View all <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                                </Link>
                            </div>

                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                                {relatedProducts.map((p) => (
                                    <Link key={p.id} to={`/shop/product/${p.slug}`} className="group">
                                        <div className="aspect-[4/5] bg-white rounded-3xl border border-black/5 overflow-hidden mb-4 shadow-sm group-hover:shadow-lg transition-all duration-500">
                                            <img src={p.image || "/api/placeholder/300/375"} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        </div>
                                        <h3 className="text-lg font-medium font-serif">{p.name}</h3>
                                        <p className="text-jazz-brown font-bold text-sm">${Number(p.price).toFixed(2)}</p>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                </div>
            </main>

            <JazzFooter />
        </div>
    );
}
